import React, { useRef, useState, useEffect } from 'react';
import { SignalingClient } from '../common/signaling';
import { PEER_CONFIG, generateRoomId } from '../common/webrtc-config';
import { RoomSetup } from './components/RoomSetup';
import { Controls } from './components/Controls';
import { VideoSection } from './components/VideoSection';
import './styles/App.css';

function App() {
  /** @type {React.Ref<HTMLVideoElement>} */
  const localVideoRef = useRef(null);
  /** @type {React.Ref<HTMLVideoElement>} */
  const remoteVideoRef = useRef(null);
  /** @type {React.RefObject<SignalingClient>} */
  const signalingRef = useRef(null);
  /** @type {React.RefObject<RTCPeerConnection>} */
  const peerConnectionRef = useRef(null);
  /** @type {React.RefObject<MediaStream>} */
  const localStreamRef = useRef(null);
  
  const [isConnected, setIsConnected] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [hasRemotePeer, setHasRemotePeer] = useState(false);
  const [logs, setLogs] = useState([]);
  const [currentRoom, setCurrentRoom] = useState('');
  const [isCreator, setIsCreator] = useState(false);
  const [inputRoomId, setInputRoomId] = useState('');

  const addLog = (message) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    console.log(message);
  };

  useEffect(() => {
    signalingRef.current = new SignalingClient();
    
    // Setup signaling event listeners
    signalingRef.current.on('connected', (socketId) => {
      addLog('Connected to signaling server: ' + socketId);
      setIsConnected(true);
    });

    signalingRef.current.on('disconnected', () => {
      addLog('Disconnected from signaling server');
      setIsConnected(false);
    });

    signalingRef.current.on('room-created', handleRoomCreated);
    signalingRef.current.on('room-joined', handleRoomJoined);
    signalingRef.current.on('room-error', handleRoomError);
    signalingRef.current.on('peer-joined', handlePeerJoined);
    signalingRef.current.on('peer-left', handlePeerLeft);
    signalingRef.current.on('offer', handleOffer);
    signalingRef.current.on('answer', handleAnswer);
    signalingRef.current.on('ice-candidate', handleIceCandidate);

    signalingRef.current.connect();

    return () => {
      if (signalingRef.current) {
        signalingRef.current.disconnect();
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const createRoom = () => {
    const newRoomId = inputRoomId || generateRoomId();
    addLog(`Creating room: ${newRoomId}`);
    signalingRef.current.createRoom(newRoomId);
  };

  const joinRoom = () => {
    if (!inputRoomId) {
      addLog('Please enter a room ID');
      return;
    }
    addLog(`Joining room: ${inputRoomId}`);
    signalingRef.current.joinRoom(inputRoomId);
  };

  const leaveRoom = () => {
    if (currentRoom) {
      addLog(`Leaving room: ${currentRoom}`);
      signalingRef.current.leaveRoom(currentRoom);
    }
    resetRoomState();
  };

  const resetRoomState = () => {
    setCurrentRoom('');
    setIsCreator(false);
    setHasRemotePeer(false);
    stopScreenShare();
    
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
  };

  const createPeerConnection = () => {
    try {
      const pc = new RTCPeerConnection(PEER_CONFIG);
      
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          addLog('Sending ICE candidate');
          signalingRef.current.sendIceCandidate(currentRoom, event.candidate);
        }
      };

      pc.ontrack = (event) => {
        addLog('Received remote track: ' + event.track.kind);
        if (event.streams && event.streams[0]) {
          addLog('Setting remote video stream');
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
            setHasRemotePeer(true);
          }
        }
      };

      pc.onconnectionstatechange = () => {
        addLog('Connection state: ' + pc.connectionState);
      };

      // Add existing local stream if we have one
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          pc.addTrack(track, localStreamRef.current);
        });
      }

      peerConnectionRef.current = pc;
      return pc;
    } catch (error) {
      addLog('Error creating peer connection: ' + error.message);
    }
  };

  /**
   * @param {'screen' | 'camera'} [source]
   */
  const startScreenShare = async (source = 'screen') => {
    try {
      addLog('Requesting screen share...');
      
      const getStream = async () => {
        if (source === 'screen') {
          console.log('screen share')
          return navigator.mediaDevices.getDisplayMedia({
            video: {
              cursor: 'always',
              displaySurface: 'window'
            },
            audio: true
          })
        }
        console.log('camera share')
        return navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        })
      }
      const stream = await getStream()

      addLog('Screen share acquired');
      localStreamRef.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      let pc = peerConnectionRef.current;
      if (!pc) {
        pc = createPeerConnection();
      } else {
        stream.getTracks().forEach(track => {
          addLog('Adding local track: ' + track.kind);
          pc.addTrack(track, stream);
        });
      }

      stream.getVideoTracks()[0].onended = () => {
        addLog('Screen share ended by user');
        stopScreenShare();
      };

      setIsSharing(true);
      
      if (hasRemotePeer) {
        await createAndSendOffer();
      }

    } catch (error) {
      addLog('Error starting screen share: ' + error.message);
    }
  };

  const createAndSendOffer = async () => {
    if (!peerConnectionRef.current) return;
    
    try {
      addLog('Creating offer...');
      const offer = await peerConnectionRef.current.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await peerConnectionRef.current.setLocalDescription(offer);
      
      addLog('Sending offer to room: ' + currentRoom);
      signalingRef.current.sendOffer(currentRoom, offer);
    } catch (error) {
      addLog('Error creating/sending offer: ' + error.message);
    }
  };

  const stopScreenShare = () => {
    addLog('Stopping screen share');
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    
    if (peerConnectionRef.current) {
      const senders = peerConnectionRef.current.getSenders();
      senders.forEach(sender => {
        if (sender.track) {
          peerConnectionRef.current.removeTrack(sender);
        }
      });
    }
    
    setIsSharing(false);
  };

  const handleRoomCreated = (data) => {
    addLog(`Room created successfully: ${data.roomId}`);
    setCurrentRoom(data.roomId);
    setIsCreator(true);
    createPeerConnection();
  };

  const handleRoomJoined = (data) => {
    addLog(`Joined room successfully: ${data.roomId}`);
    setCurrentRoom(data.roomId);
    setIsCreator(false);
    createPeerConnection();
  };

  const handleRoomError = (data) => {
    addLog(`Room error: ${data.error}`);
    alert(`Room error: ${data.error}`);
  };

  const handlePeerJoined = (data) => {
    addLog(`Peer joined: ${data.peerId}`);
    setHasRemotePeer(true);
    
    if (isSharing) {
      setTimeout(() => createAndSendOffer(), 1000);
    }
  };

  const handlePeerLeft = (data) => {
    addLog(`Peer left: ${data.peerId}`);
    setHasRemotePeer(false);
    
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
  };

  const handleOffer = async (data) => {
    addLog('Received offer from: ' + data.from);
    
    if (!peerConnectionRef.current) {
      createPeerConnection();
    }

    const pc = peerConnectionRef.current;
    
    try {
      await pc.setRemoteDescription(data.offer);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      signalingRef.current.sendAnswer(data.roomId, answer);
    } catch (error) {
      addLog('Error handling offer: ' + error.message);
    }
  };

  const handleAnswer = async (data) => {
    addLog('Received answer from: ' + data.from);
    
    if (peerConnectionRef.current && peerConnectionRef.current.signalingState === 'have-local-offer') {
      await peerConnectionRef.current.setRemoteDescription(data.answer);
      addLog('Remote description set successfully');
    }
  };

  const handleIceCandidate = async (data) => {
    addLog('Received ICE candidate from: ' + data.from);
    
    if (peerConnectionRef.current && data.candidate) {
      try {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
      } catch (error) {
        addLog('Error adding ICE candidate: ' + error.message);
      }
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>WebRTC Screen Sharing (Web)</h1>
        <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
          Status: {isConnected ? 'Connected' : 'Disconnected'}
          {currentRoom && ` | Room: ${currentRoom} (${isCreator ? 'Creator' : 'Participant'})`}
          {hasRemotePeer && ' | Peer Connected'}
        </div>
      </header>

      {!currentRoom ? (
        <RoomSetup
          inputRoomId={inputRoomId}
          setInputRoomId={setInputRoomId}
          createRoom={createRoom}
          joinRoom={joinRoom}
        />
      ) : (
        <>
          <Controls
            startScreenShare={() => startScreenShare('screen')}
            startCamera={() => startScreenShare('camera')}
            stopScreenShare={stopScreenShare}
            leaveRoom={leaveRoom}
            isConnected={isConnected}
            isSharing={isSharing}
          />

          <div className="video-container">
            <VideoSection
              title={`Your Screen ${isCreator ? '(Creator)' : ''}`}
              videoRef={localVideoRef}
              isSharing={isSharing}
              isLocal={true}
            />
            <VideoSection
              title={`Remote Peer ${!isCreator ? '(Creator)' : ''}`}
              videoRef={remoteVideoRef}
              hasRemotePeer={hasRemotePeer}
              isCreator={isCreator}
            />
          </div>

          <div className="instructions">
            <h4>Web Instructions:</h4>
            <ul>
              <li>Share your entire screen or application window</li>
              <li>Both users can share screens simultaneously</li>
              <li>Use Chrome/Firefox for best compatibility</li>
            </ul>
          </div>
        </>
      )}

      <div className="logs">
        <h3>Connection Logs</h3>
        <div className="log-container">
          {logs.map((log, index) => (
            <div key={index} className="log-entry">{log}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default App;